import { createElement, Fragment, ReactNode } from "react";
import { ContractTerm, ContractTermPrice } from "../types/contracts";
import { Equipment, EquipmentCounter } from "../types/equipments";
import { Settlement, SettlementPending } from "../types/settlements";

export const COMMON_COUNTER_KIND = "CCR";
export const SCAN_COUNTER_KIND = "SP";

type SettlementTermSource = Pick<Settlement, "contract_term"> | Pick<SettlementPending, "contract_term">;

type SettlementEquipmentRef = Pick<Equipment, "id" | "full_name">;

export function getSettlementTermEquipments(term: ContractTerm): SettlementEquipmentRef[] {
  return (term.equipments ?? []).map((equipment) => ({
    id: equipment.id,
    full_name: equipment.full_name,
  }));
}

export function getSettlementTermPrimaryEquipmentId(term: ContractTerm): string | undefined {
  const equipmentId = getSettlementTermEquipments(term)[0]?.id;
  return equipmentId != null ? String(equipmentId) : undefined;
}

export function getSettlementEquipments(
  settlement: SettlementTermSource
): SettlementEquipmentRef[] {
  const term = settlement.contract_term;
  if (!term) {
    return [];
  }
  return getSettlementTermEquipments(term);
}

export function getSettlementEquipmentNames(
  settlement: SettlementTermSource
): string[] {
  return getSettlementEquipments(settlement)
    .map((equipment) => equipment.full_name || "")
    .filter(Boolean);
}

export function getSettlementEquipmentLabel(
  settlement: SettlementTermSource,
  fallback = "-"
): string {
  const names = getSettlementEquipmentNames(settlement);
  if (names.length === 0) {
    return fallback;
  }
  return names.join(", ");
}

export function getSettlementDisplayLabel(
  settlement: SettlementTermSource,
  t: (key: string, options?: { count?: number }) => string,
  fallback = "-"
): string {
  const term = settlement.contract_term;
  if (term && isCommonCounterContractTerm(term)) {
    return getContractTermDisplayLabel(term, t, fallback);
  }
  return getSettlementEquipmentLabel(settlement, fallback);
}

export function getSettlementContractId(
  settlement: SettlementTermSource
): string | undefined {
  const contractId = settlement.contract_term?.contract;
  if (contractId != null) {
    return String(contractId);
  }
  return undefined;
}

export function getSettlementContractNumber(
  settlement: SettlementTermSource
): string {
  return settlement.contract_term?.contract_info?.number || "-";
}

export function getSettlementContractInternalName(
  settlement: SettlementTermSource
): string {
  return settlement.contract_term?.contract_info?.internal_name || "";
}

export function getSettlementContractLabel(
  settlement: SettlementTermSource,
  fallback = "-"
): string {
  const contractNumber = settlement.contract_term?.contract_info?.number || "";
  const contractInternalName = getSettlementContractInternalName(settlement);

  if (contractNumber && contractInternalName) {
    return `${contractNumber} - ${contractInternalName}`;
  }

  return contractNumber || contractInternalName || fallback;
}

export function getSettlementClientName(
  settlement: SettlementTermSource
): string {
  return settlement.contract_term?.client_name || "-";
}

export function isCommonCounterSettlement(
  settlement: SettlementTermSource
): boolean {
  return settlement.contract_term?.kind === COMMON_COUNTER_KIND;
}

export function getSettlementPrimaryContractTerm(
  settlement: SettlementTermSource
): ContractTerm | undefined {
  return settlement.contract_term;
}

export function getEquipmentIdForPendingPosition(
  settlement: SettlementPending,
  positionIndex: number
): string | undefined {
  const position = settlement.positions?.[positionIndex];
  if (position?.equipment_id != null) {
    return String(position.equipment_id);
  }

  const term = settlement.contract_term;
  if (!term) {
    return undefined;
  }

  const equipments = getSettlementTermEquipments(term);
  if (equipments.length === 1) {
    const equipmentId = equipments[0]?.id;
    return equipmentId != null ? String(equipmentId) : undefined;
  }

  return undefined;
}

export function getContractTermEquipmentLabel(
  term: Pick<ContractTerm, "equipments">,
  fallback = "-"
): string {
  const names = (term.equipments ?? [])
    .map((equipment: Equipment) => equipment.full_name)
    .filter(Boolean);
  return names.length > 0 ? names.join(", ") : fallback;
}

export function isCommonCounterContractTerm(
  term: Pick<ContractTerm, "kind">
): boolean {
  return term.kind === COMMON_COUNTER_KIND;
}

export function getContractTermDisplayLabel(
  term: Pick<ContractTerm, "kind" | "equipments">,
  t: (key: string, options?: { count?: number }) => string,
  fallback = "-"
): string {
  if (isCommonCounterContractTerm(term)) {
    return t("Common counter for {{count}} equipments", {
      count: term.equipments?.length ?? 0,
    });
  }
  return getContractTermEquipmentLabel(term, fallback);
}

export type ContractTermPriceEquipmentGroup = {
  equipmentId: string;
  equipmentName: string;
  prices: ContractTermPrice[];
};

export function groupContractTermPricesByEquipment(
  term: ContractTerm
): ContractTermPriceEquipmentGroup[] {
  const prices = term.prices ?? [];
  if (!isCommonCounterContractTerm(term) || prices.length === 0) {
    return [];
  }

  const equipmentById = new Map(
    (term.equipments ?? []).map((equipment) => [String(equipment.id), equipment])
  );
  const groups = new Map<string, ContractTermPriceEquipmentGroup>();

  for (const price of prices) {
    const startCounter = price.start_counter as EquipmentCounter | string;
    const equipmentId =
      typeof startCounter !== "string" && startCounter?.equipment_id != null
        ? String(startCounter.equipment_id)
        : "unknown";
    const equipment = equipmentById.get(equipmentId);
    const existingGroup = groups.get(equipmentId);

    if (existingGroup) {
      existingGroup.prices.push(price);
      continue;
    }

    groups.set(equipmentId, {
      equipmentId,
      equipmentName: equipment?.full_name || equipmentId,
      prices: [price],
    });
  }

  const orderedEquipments = term.equipments ?? [];
  if (orderedEquipments.length > 0) {
    return orderedEquipments
      .map((equipment) => groups.get(String(equipment.id)))
      .filter((group): group is ContractTermPriceEquipmentGroup => Boolean(group));
  }

  return Array.from(groups.values());
}

// Ids of equipments that have a priced counter of the given kind on this
// term - used to decide whether e.g. "Add Scan counter" makes sense at all,
// rather than just whether one has already been added.
export function getContractTermPricedEquipmentIds(
  term: { prices?: ContractTermPrice[]; prices_info?: ContractTermPrice[] } | undefined | null,
  kind: string
): Set<string> {
  const prices = term?.prices ?? term?.prices_info ?? [];
  const ids = new Set<string>();

  for (const price of prices) {
    const startCounter = price.start_counter as EquipmentCounter | string | null;
    if (!startCounter || typeof startCounter === "string") {
      continue;
    }
    if (startCounter.kind === kind && startCounter.equipment_id != null) {
      ids.add(String(startCounter.equipment_id));
    }
  }

  return ids;
}

// Resolves the ContractTermPrice id billing a given equipment/kind pair on
// this term - used to pin a just-entered counter value to the specific
// settlement position it was meant to resolve (see overridePositionCounter
// in settlementCounterActions.ts), regardless of which price row generated
// the position.
export function findContractTermPriceId(
  term: { prices?: ContractTermPrice[]; prices_info?: ContractTermPrice[] } | undefined | null,
  equipmentId: string | number | undefined | null,
  kind: string
): string | number | undefined {
  if (equipmentId == null) {
    return undefined;
  }

  const prices = term?.prices ?? term?.prices_info ?? [];
  const match = prices.find((price) => {
    const startCounter = price.start_counter as EquipmentCounter | string | null;
    return (
      startCounter &&
      typeof startCounter !== "string" &&
      startCounter.kind === kind &&
      startCounter.equipment_id != null &&
      String(startCounter.equipment_id) === String(equipmentId)
    );
  });

  return match?.id;
}

export function contractTermHasScanPrice(
  term: { prices?: ContractTermPrice[]; prices_info?: ContractTermPrice[] } | undefined | null,
  equipmentId?: string | number | null
): boolean {
  const pricedEquipmentIds = getContractTermPricedEquipmentIds(term, SCAN_COUNTER_KIND);
  if (equipmentId == null) {
    return pricedEquipmentIds.size > 0;
  }
  return pricedEquipmentIds.has(String(equipmentId));
}

// Equipment full_name is server-formatted as "{producer} {model} ({serial})" -
// bolding just the serial means picking it back out of that string rather
// than reassembling it, since callers only have full_name + serial to work with.
export function renderEquipmentFullName(
  equipment?: { full_name?: string; serial?: string } | null,
  fallback: ReactNode = "-"
): ReactNode {
  const fullName = equipment?.full_name;
  if (!fullName) {
    return fallback;
  }

  const serial = equipment?.serial;
  const serialSuffix = serial ? `(${serial})` : "";
  if (serial && fullName.endsWith(serialSuffix)) {
    const prefix = fullName.slice(0, fullName.length - serialSuffix.length);
    return createElement(
      Fragment,
      null,
      prefix,
      "(",
      createElement("strong", null, serial),
      ")"
    );
  }

  return fullName;
}

export function renderEquipmentFullNameList(
  equipments: Array<{ full_name?: string; serial?: string }> | undefined | null,
  fallback: ReactNode = "-"
): ReactNode {
  if (!equipments || equipments.length === 0) {
    return fallback;
  }

  return createElement(
    Fragment,
    null,
    ...equipments.flatMap((equipment, index) => [
      index > 0 ? ", " : null,
      renderEquipmentFullName(equipment),
    ])
  );
}
